/**
 * Marketplace store module (Phase 1 — public browse + detail).
 *
 * Holds the catalog card list, a per-id detail cache, and the client-side
 * filter state (free-text search + single-select category + multi-select
 * tags). Filtering is done entirely in the browser: the public list endpoint
 * returns the full (small) catalog, so `filteredCards` is cheap and instant.
 *
 * Namespaced and NOT persisted — this is live data re-fetched on entry.
 */
import { marketplaceApi } from '@/services/marketplace-api'

function norm(value) {
  return String(value || '').toLowerCase()
}

const state = () => ({
  cards: [], // card[] from listCatalog
  cardsLoading: false,
  cardsError: null,
  loaded: false, // true once a successful list fetch has completed

  detailCache: {}, // id -> detail object (memoized)
  detailLoading: false,
  detailError: null, // { type: 'notfound' | 'error', message }

  search: '', // free-text query (name / short_description / keywords / tags)
  activeCategory: null, // null = "All"
  activeTags: [] // multi-select tag chips, combined with AND
})

const getters = {
  /**
   * Unique sorted categories with per-category card counts.
   * Returns [{ key, label, count }] sorted alphabetically by label.
   */
  categories(state) {
    const counts = new Map()
    state.cards.forEach((card) => {
      const cat = card.category || 'other'
      counts.set(cat, (counts.get(cat) || 0) + 1)
    })
    return Array.from(counts.entries())
      .map(([key, count]) => ({
        key,
        label: key.charAt(0).toUpperCase() + key.slice(1),
        count
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  },

  /**
   * Unique tags across all cards with counts, sorted by count desc then name.
   * Returns [{ tag, count }].
   */
  allTags(state) {
    const counts = new Map()
    state.cards.forEach((card) => {
      (card.tags || []).forEach((tag) => {
        counts.set(tag, (counts.get(tag) || 0) + 1)
      })
    })
    return Array.from(counts.entries())
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag))
  },

  /**
   * Cards filtered by search + category + active tags (all client-side).
   * Search matches case-insensitively across name + short_description +
   * keywords + tags. Category is equality. Every active tag must be present.
   */
  filteredCards(state) {
    const q = state.search.trim().toLowerCase()
    return state.cards.filter((card) => {
      if (state.activeCategory && card.category !== state.activeCategory) {
        return false
      }
      if (state.activeTags.length) {
        const cardTags = (card.tags || []).map(norm)
        const everyTagPresent = state.activeTags.every((t) =>
          cardTags.includes(norm(t))
        )
        if (!everyTagPresent) return false
      }
      if (q) {
        const haystack = [
          card.name,
          card.short_description,
          ...(card.keywords || []),
          ...(card.tags || [])
        ]
          .map(norm)
          .join(' ')
        if (!haystack.includes(q)) return false
      }
      return true
    })
  },

  /** True when any filter (search / category / tags) is active. */
  hasActiveFilters(state) {
    return Boolean(
      state.search.trim() || state.activeCategory || state.activeTags.length
    )
  }
}

const mutations = {
  SET_CARDS_LOADING(state, v) {
    state.cardsLoading = v
  },
  SET_CARDS_ERROR(state, v) {
    state.cardsError = v
  },
  SET_CARDS(state, cards) {
    state.cards = Array.isArray(cards) ? cards : []
    state.loaded = true
  },
  SET_DETAIL_LOADING(state, v) {
    state.detailLoading = v
  },
  SET_DETAIL_ERROR(state, v) {
    state.detailError = v
  },
  CACHE_DETAIL(state, { id, detail }) {
    state.detailCache = { ...state.detailCache, [id]: detail }
  },
  SET_SEARCH(state, term) {
    state.search = term || ''
  },
  SET_CATEGORY(state, category) {
    state.activeCategory = category || null
  },
  TOGGLE_TAG(state, tag) {
    const idx = state.activeTags.indexOf(tag)
    if (idx === -1) {
      state.activeTags = [...state.activeTags, tag]
    } else {
      state.activeTags = state.activeTags.filter((t) => t !== tag)
    }
  },
  CLEAR_FILTERS(state) {
    state.search = ''
    state.activeCategory = null
    state.activeTags = []
  }
}

const actions = {
  /** Load the catalog card list. Safe to call repeatedly (idempotent). */
  async loadCatalog({ commit }) {
    commit('SET_CARDS_LOADING', true)
    commit('SET_CARDS_ERROR', null)
    try {
      const cards = await marketplaceApi.listCatalog()
      commit('SET_CARDS', cards)
    } catch (error) {
      console.error('Failed to load marketplace catalog:', error)
      commit('SET_CARDS_ERROR', error.message || 'Failed to load the marketplace')
    } finally {
      commit('SET_CARDS_LOADING', false)
    }
  },

  /**
   * Load one app's detail. Serves the in-memory cache when present (the
   * service layer also revalidates via ETag), otherwise fetches.
   */
  async loadCatalogDetail({ commit, state }, id) {
    commit('SET_DETAIL_ERROR', null)
    if (state.detailCache[id]) {
      // Serve cached immediately; no spinner flash on revisits.
      return state.detailCache[id]
    }
    commit('SET_DETAIL_LOADING', true)
    try {
      const detail = await marketplaceApi.getCatalog(id)
      commit('CACHE_DETAIL', { id, detail })
      return detail
    } catch (error) {
      if (error && error.notFound) {
        commit('SET_DETAIL_ERROR', { type: 'notfound', message: error.message })
      } else {
        console.error('Failed to load marketplace app detail:', error)
        commit('SET_DETAIL_ERROR', {
          type: 'error',
          message: error.message || 'Failed to load this app'
        })
      }
      return null
    } finally {
      commit('SET_DETAIL_LOADING', false)
    }
  },

  setSearch({ commit }, term) {
    commit('SET_SEARCH', term)
  },
  setCategory({ commit }, category) {
    commit('SET_CATEGORY', category)
  },
  toggleTag({ commit }, tag) {
    commit('TOGGLE_TAG', tag)
  },
  clearFilters({ commit }) {
    commit('CLEAR_FILTERS')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
